import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface UploadBoletoFormProps {
  obligationId: string;
  onSuccess: () => void;
  onCancel: () => void;
  uploadBoletoMutation: {
    mutate: (
      args: { obligationId: string; boletoNumber: string; file: File },
      opts?: { onSuccess?: () => void }
    ) => void;
    isPending: boolean;
  };
  defaultBoletoNumber?: string;
}

export function UploadBoletoForm({
  obligationId,
  onSuccess,
  onCancel,
  uploadBoletoMutation,
  defaultBoletoNumber = "",
}: UploadBoletoFormProps): JSX.Element {
  const { t } = useTranslation();
  const [boletoNumber, setBoletoNumber] = useState(defaultBoletoNumber);
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const num = boletoNumber.trim();
    if (!num || !file) return;
    uploadBoletoMutation.mutate(
      { obligationId, boletoNumber: num, file },
      { onSuccess: () => onSuccess() }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="upload-boleto-number">
          {t("estadoFiscal.uploadBoletoNumberLabel")}
        </Label>
        <Input
          id="upload-boleto-number"
          value={boletoNumber}
          onChange={(e) => setBoletoNumber(e.target.value)}
          placeholder={t("estadoFiscal.uploadBoletoNumberPlaceholder")}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="upload-boleto-file">
          {t("estadoFiscal.uploadBoletoFileLabel")}
        </Label>
        <Input
          id="upload-boleto-file"
          type="file"
          accept=".pdf,image/*"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          required
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("estadoFiscal.dialogCancel")}
        </Button>
        <Button
          type="submit"
          disabled={!boletoNumber.trim() || !file || uploadBoletoMutation.isPending}
        >
          {uploadBoletoMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          {t("estadoFiscal.uploadBoletoSubmit")}
        </Button>
      </div>
    </form>
  );
}
